import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import {
  API_AUDIO_CHAT,
  API_VIDEO_CHAT,
  apiStudentStudys,
  apiMyAssestsExams,
  apiHideExam,
  apiUnHideExam,
  API_EXAM_SUBJECTS,
  API_EXAM_CONTENT_CHAPTERS,
} from "../../utils/api-routes";

const STATUS = [
  { key: "ready", value: "Ready" },
  { key: "generating", value: "Generating" },
  // { key: "failed", value: "Failed" },
];
// const LANGUAGES = [
//   { key: "Tamil", value: "Tamil" },
//   { key: "English", value: "English" },
// ];
const LANGUAGES = [
  { key: "English", value: "English" },
  { key: "Bengali", value: "Bengali" },
  { key: "Gujarati", value: "Gujarati" },
  { key: "Hindi", value: "Hindi" },
  { key: "Kannada", value: "Kannada" },
  { key: "Malayalam", value: "Malayalam" },
  { key: "Marathi", value: "Marathi" },
  { key: "Punjabi", value: "Punjabi" },
  { key: "Tamil", value: "Tamil" },
  { key: "Telugu", value: "Telugu" },
  { key: "Urdu", value: "Urdu" },
];
const SORT = [
  { key: "recent", value: "Recent" },
  { key: "oldest", value: "Oldest" },
];

const useHandler = () => {
  const { get, post, put } = useApi();
  const navigate = useNavigate();
  const { mail, user, board, role, examType } = useSingleton();
  const [apiAssets, setApiAssets] = useState([]);
  const [popupMedia, setPopupMedia] = useState(null);
  const [showAskBox, setShowAskBox] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [audioRes, setAudioRes] = useState({});
  const [videoRes, setVideoRes] = useState({});
  const [classdetails, setClassDetails] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");

  const inputRef = useRef(null);
  const [filters, setFilters] = useState({
    type: "all",
    status: "",
    language: "",
    sort: "recent",
    subject: "",
    chapter: "",
    include_hidden: false,
    dateMode: "specific",
    on_date: "",
    from_date: "",
    to_date: "",
  });
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  // Helper to extract exam target - prioritizes 'focus' from /api/users/info for guest user
  const getExamTarget = () => {
    return (
      user?.data?.focus ||
      classdetails?.data?.focus ||
      examType ||
      user?.data?.exam_type ||
      user?.data?.class ||
      classdetails?.data?.class
    );
  };

  const myAssetsApiWithFilters = async (currentFilters) => {
    const exam = getExamTarget();
    if (!exam || !mail) return;

    setApiAssets([]);
    try {
      const res = await get(
        apiMyAssestsExams(
          mail,
          exam,
          currentFilters.type,
          currentFilters.status,
          currentFilters.language,
          currentFilters.sort,
          currentFilters.subject,
          currentFilters.chapter,
          currentFilters.include_hidden || undefined,
          currentFilters.dateMode === "specific"
            ? currentFilters.on_date
            : undefined,
          currentFilters.dateMode === "range"
            ? currentFilters.from_date
            : undefined,
          currentFilters.dateMode === "range"
            ? currentFilters.to_date
            : undefined,
        ),
      );
      if (res) {
        setApiAssets(res?.data?.assets || []);
      }
    } catch (e) {
      console.log("ERROR COMES FROM MYASSESTS WITH FILTERS", e);
    }
  };

  useEffect(() => {
    if (!mail) return;
    const exam = getExamTarget();
    if (!exam) return;
    myAssetsApiWithFilters(filters);
  }, [filters, user, classdetails, examType]);

  const toggleHideAsset = async (asset_id, isHidden) => {
    try {
      if (isHidden) {
        await put(apiUnHideExam(asset_id)); // Unhide
      } else {
        await put(apiHideExam(asset_id)); // Hide
      }

      // Refresh asset list after update
      myAssetsApiWithFilters(filters);
    } catch (e) {
      console.log("Error toggling hide asset", e);
    }
  };

  const aiAudioChat = async (filters, audio_content) => {
    const payload = {
      Audio_content: audio_content,
      language: audio_content?.language,
      Question: question,
    };

    try {
      const res = await post(API_AUDIO_CHAT, payload);
      if (res) {
        setAudioRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AUDIO CHAT API", e);
    }
  };

  const aiVideoChat = async (video_content) => {
    const payload = {
      // Video_content: video_content,
      language: video_content?.language,
      filters: video_content,
      Question: question,
    };

    try {
      const res = await post(API_VIDEO_CHAT, payload);
      if (res) {
        setVideoRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AI VIDEO CHAT API", e);
    }
  };

  const getUserStudiesDetails = async () => {
    if (!mail) return;
    try {
      const res = await get(apiStudentStudys(mail));
      if (res) {
        // console.log(res?.data)
        setClassDetails(res?.data);
        const examFromInfo =
          res?.data?.data?.focus ||
          res?.data?.data?.class ||
          res?.data?.data?.exam_type;
        const payload = {
          board: board || res?.data?.data?.board,
          exam: examFromInfo,
        };
        getSubjectDetails(payload);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      console.log("Error on getting user studiesdetails", e);
    }
  };

  useEffect(() => {
    getUserStudiesDetails();
  }, [mail]);

  const getSubjectDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_EXAM_SUBJECTS, payload);
      if (res) {
        setSubjects(res?.data?.subjects);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getChapterDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_EXAM_CONTENT_CHAPTERS, payload);
      if (res) {
        setChapters(res?.data?.chapters);
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting chapeter details", e);
    }
  };

  useEffect(() => {
    const targetExam =
      user?.data?.focus ||
      classdetails?.data?.focus ||
      user?.data?.class ||
      classdetails?.data?.class;
    if (!selectedSubject || !targetExam) return;

    const payload = {
      board: board || user?.data?.board || classdetails?.data?.board,
      exam: targetExam,
      subject: selectedSubject,
    };
    getChapterDetails(payload);
  }, [selectedSubject, user, classdetails]);

  return {
    SORT,
    STATUS,
    mail,
    apiAssets,
    question,
    showAskBox,
    popupMedia,
    filters,
    audioRef,
    videoRef,
    audioRes,
    videoRes,
    inputRef,
    subjects,
    chapters,
    role,
    setSelectedSubject,
    setPopupMedia,
    setShowAskBox,
    setApiAssets,
    setQuestion,
    setFilters,
    navigate,
    aiVideoChat,
    aiAudioChat,
    toggleHideAsset,
    LANGUAGES,
  };
};

export default useHandler;
